/**
 * The one source of "when did the user last do something".
 *
 * Two mechanisms depend on this and MUST agree: the idle logout (`useInactivityTimeout`)
 * and the proactive session refresh (`session-keeper`). If each owned its own listeners
 * they could drift — a refresh keeping alive a session the idle flow considers abandoned,
 * or an idle logout firing on a user the refresh just called active. One store, one
 * definition of "active", and the invariant holds: the token is never refreshed for a
 * user the idle logout would consider gone.
 *
 * Module-level rather than React state on purpose: the scheduler is not a component, and
 * `mousemove` fires far too often to route through a render.
 */

/** How long without activity before the user counts as idle (9 min). */
export const IDLE_WINDOW_MS = 540_000;

/** `mousemove` fires continuously; one mark per second is plenty for a 9-minute window. */
const MARK_THROTTLE_MS = 1_000;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click'] as const;

type Listener = () => void;

let lastActivityAt = Date.now();
let lastMarkedAt = 0;
let stopTracking: (() => void) | null = null;
const listeners = new Set<Listener>();

export const markActivity = (now: number = Date.now()) => {
	lastActivityAt = now;
	if (now - lastMarkedAt < MARK_THROTTLE_MS) return;
	lastMarkedAt = now;
	listeners.forEach((listener) => listener());
};

export const getLastActivityAt = () => lastActivityAt;

export const msSinceActivity = (now: number = Date.now()) => Math.max(0, now - lastActivityAt);

export const isUserActive = (now: number = Date.now(), idleWindowMs: number = IDLE_WINDOW_MS) =>
	msSinceActivity(now) < idleWindowMs;

/** Notified (throttled) on every activity mark. Returns the unsubscribe. */
export const subscribeToActivity = (listener: Listener) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

/**
 * Attach the window listeners once. Idempotent — every consumer may call it, only the
 * first attaches anything. Returns a release that detaches only when the last consumer
 * has released.
 */
let trackingConsumers = 0;

export const startActivityTracking = () => {
	trackingConsumers += 1;
	if (!stopTracking) {
		const onActivity = () => markActivity();
		ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onActivity));
		stopTracking = () => {
			ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
		};
	}
	let released = false;
	return () => {
		if (released) return;
		released = true;
		trackingConsumers -= 1;
		if (trackingConsumers <= 0 && stopTracking) {
			stopTracking();
			stopTracking = null;
			trackingConsumers = 0;
		}
	};
};

/** Test seam only. */
export const __resetActivityForTests = () => {
	lastActivityAt = Date.now();
	lastMarkedAt = 0;
	listeners.clear();
	if (stopTracking) stopTracking();
	stopTracking = null;
	trackingConsumers = 0;
};
